import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, filter, from, map, of, switchMap, withLatestFrom } from 'rxjs';

import {
    selectAppModeIfDefined,
    selectInputModeIfDefined,
} from 'app/features/settings/state/settings/settings.selectors';
import { createTimeRegistrationActivity } from 'app/shared/components/system-and-break-activity/models';
import { getActivityByNumber, isSupportedBreakActivity } from 'app/shared/features/activity-selector/utils';
import { selectActivityListIfLoaded } from 'app/state/activities/activity.selectors';
import { openDialog } from 'app/state/app-state/app-state.actions';
import { breakStarting } from 'app/state/break/break.actions';

import { ScanQrDialogComponent } from '../../pages/scan-qr-dialog/scan-qr-dialog.component';
import { setActivityInScanFlow, setScanFlowEmployeeNumber } from '../scan-flow/scan-flow.actions';
import { selectScanFlowAllowedCodeTypes } from '../scan-flow/scan-flow.selectors';
import { parseScannedCode } from '../scanned-codes/utils/scanned-code.utils';
import { openScanQrDialog, processScannedCode, setScanFlowScannedCodes } from './scanned-codes.actions';

/*
    Effects for the scanned codes feature.
    This includes opening the scan dialog and processing scanned codes.
 */

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class ScannedCodesEffects {
    /**
     * Initialise a scan dialog to scan new code
     * It can be triggered when:
     *  - user clicks on the scan button in footer (scan any entity e.g. activity, container etc)
     *  - user sign in either in login, wizard setup or settings page (saves employee code)
     */
    initialiseScanDialog$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(openScanQrDialog),
            withLatestFrom(this.store.pipe(selectInputModeIfDefined)),
            filter(([, inputMode]) => inputMode === 'barcode'),
            map(([{ token, onScanFinishedAction, allowedCodeTypes }]) =>
                openDialog({
                    dialog: {
                        component: ScanQrDialogComponent,
                        inputs: {
                            allowedCodeTypes,
                            scannedCodeToken: token,
                            onScanFinishedAction,
                        },
                    },
                }),
            ),
        );
    });

    processScannedCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(processScannedCode),
            withLatestFrom(
                this.store.pipe(selectActivityListIfLoaded),
                this.store.pipe(selectScanFlowAllowedCodeTypes),
                this.store.pipe(selectAppModeIfDefined),
            ),
            switchMap(([{ scannedText }, activityList, allowedCodeTypes, appMode]) => {
                const scannedCode = parseScannedCode({ code: scannedText, allowedCodeTypes });

                if (scannedCode.error) {
                    return EMPTY;
                }

                if (scannedCode.codeType !== 'activity') {
                    if (scannedCode.codeType === 'employee') {
                        return from([
                            setScanFlowEmployeeNumber({
                                employeeNumber: scannedCode.number ?? undefined,
                            }),
                            setScanFlowScannedCodes({ scannedCode }),
                        ]);
                    }
                    return of(setScanFlowScannedCodes({ scannedCode }));
                }
                /**
                 * Activity number can be >= 1 when it's a normal activity (End Break / Labor Registration / Container Measurement)
                 * When activity number is 0 which represents Login/Logout/StartBreak we want to check if there's a system code
                 * Multiuser mode has system codes for login/logout/start break
                 * Personal mode doesn't have system codes and uses number 0 for those activities instead
                 */
                if (scannedCode.number !== undefined) {
                    const activity = getActivityByNumber(activityList, scannedCode.number);
                    if (appMode === 'multi-user' && scannedCode.systemCode?.isSystemCode) {
                        const newActivity = createTimeRegistrationActivity(scannedCode.systemCode.name);

                        return of(
                            setActivityInScanFlow({
                                activityCode: {
                                    ...scannedCode,
                                    entity: newActivity,
                                },
                                keepRegistrationData: true,
                            }),
                        );
                    }
                    const isBreakActivityScanned = activity && isSupportedBreakActivity(activity);
                    if (appMode === 'personal' && isBreakActivityScanned) {
                        return of(breakStarting({ activity }));
                    }

                    if (activity) {
                        return of(
                            setActivityInScanFlow({
                                activityCode: { ...scannedCode, entity: activity },
                                keepRegistrationData: true,
                            }),
                        );
                    }
                }

                return EMPTY;
            }),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store,
    ) {}
}
